/**
 * GPU Marching Cubes Surface Extraction
 *
 * ZERO CPU READBACK architecture:
 * - Reads packed voxel buffer (same as VoxelGPUCompute) directly on GPU
 * - Generates mesh vertices + normals entirely in compute shader
 * - Only reads back vertex count + vertex data for Three.js BufferGeometry
 *
 * Two-pass approach:
 *   Pass 1: Count triangles per cell (atomic counter)
 *   Pass 2: Write vertices using prefix-sum offsets
 * Simplified to single-pass with atomic append for pragmatism.
 */

import * as THREE from 'three';

// ── Marching Cubes Lookup Tables ─────────────────────────────────────────────

/** Edge table: for each of 256 cube configs, which of 12 edges are intersected */
const MC_EDGE_TABLE: number[] = [
  0x0,0x109,0x203,0x30a,0x406,0x50f,0x605,0x70c,0x80c,0x905,0xa0f,0xb06,0xc0a,0xd03,0xe09,0xf00,
  0x190,0x99,0x393,0x29a,0x596,0x49f,0x795,0x69c,0x99c,0x895,0xb9f,0xa96,0xd9a,0xc93,0xf99,0xe90,
  0x230,0x339,0x33,0x13a,0x636,0x73f,0x435,0x53c,0xa3c,0xb35,0x83f,0x936,0xe3a,0xf33,0xc39,0xd30,
  0x3a0,0x2a9,0x1a3,0xaa,0x7a6,0x6af,0x5a5,0x4ac,0xbac,0xaa5,0x9af,0x8a6,0xfaa,0xea3,0xda9,0xca0,
  0x460,0x569,0x663,0x76a,0x66,0x16f,0x265,0x36c,0xc6c,0xd65,0xe6f,0xf66,0x86a,0x963,0xa69,0xb60,
  0x5f0,0x4f9,0x7f3,0x6fa,0x1f6,0xff,0x3f5,0x2fc,0xdfc,0xcf5,0xfff,0xef6,0x9fa,0x8f3,0xbf9,0xaf0,
  0x650,0x759,0x453,0x55a,0x256,0x35f,0x55,0x15c,0xe5c,0xf55,0xc5f,0xd56,0xa5a,0xb53,0x859,0x950,
  0x7c0,0x6c9,0x5c3,0x4ca,0x3c6,0x2cf,0x1c5,0xcc,0xfcc,0xec5,0xdcf,0xcc6,0xbca,0xac3,0x9c9,0x8c0,
  0x8c0,0x9c9,0xac3,0xbca,0xcc6,0xdcf,0xec5,0xfcc,0xcc,0x1c5,0x2cf,0x3c6,0x4ca,0x5c3,0x6c9,0x7c0,
  0x950,0x859,0xb53,0xa5a,0xd56,0xc5f,0xf55,0xe5c,0x15c,0x55,0x35f,0x256,0x55a,0x453,0x759,0x650,
  0xaf0,0xbf9,0x8f3,0x9fa,0xef6,0xfff,0xcf5,0xdfc,0x2fc,0x3f5,0xff,0x1f6,0x6fa,0x7f3,0x4f9,0x5f0,
  0xb60,0xa69,0x963,0x86a,0xf66,0xe6f,0xd65,0xc6c,0x36c,0x265,0x16f,0x66,0x76a,0x663,0x569,0x460,
  0xca0,0xda9,0xea3,0xfaa,0x8a6,0x9af,0xaa5,0xbac,0x4ac,0x5a5,0x6af,0x7a6,0xaa,0x1a3,0x2a9,0x3a0,
  0xd30,0xc39,0xf33,0xe3a,0x936,0x83f,0xb35,0xa3c,0x53c,0x435,0x73f,0x636,0x13a,0x33,0x339,0x230,
  0xe90,0xf99,0xc93,0xd9a,0xa96,0xb9f,0x895,0x99c,0x69c,0x795,0x49f,0x596,0x29a,0x393,0x99,0x190,
  0xf00,0xe09,0xd03,0xc0a,0xb06,0xa0f,0x905,0x80c,0x70c,0x605,0x50f,0x406,0x30a,0x203,0x109,0x0
];

/**
 * Triangle table: for each of 256 cube configs, up to 5 triangles (15 edge indices, -1 terminated).
 * Flattened: 256 * 16 i32 values.
 */
const MC_TRI_TABLE: number[] = [
  -1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  0,8,3,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  0,1,9,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  1,8,3,9,8,1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  1,2,10,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  0,8,3,1,2,10,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  9,2,10,0,2,9,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  2,8,3,2,10,8,10,9,8,-1,-1,-1,-1,-1,-1,-1,
  3,11,2,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  0,11,2,8,11,0,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  1,9,0,2,3,11,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  1,11,2,1,9,11,9,8,11,-1,-1,-1,-1,-1,-1,-1,
  3,10,1,11,10,3,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  0,10,1,0,8,10,8,11,10,-1,-1,-1,-1,-1,-1,-1,
  3,9,0,3,11,9,11,10,9,-1,-1,-1,-1,-1,-1,-1,
  9,8,10,10,8,11,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  4,7,8,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  4,3,0,7,3,4,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  0,1,9,8,4,7,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  4,1,9,4,7,1,7,3,1,-1,-1,-1,-1,-1,-1,-1,
  1,2,10,8,4,7,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  3,4,7,3,0,4,1,2,10,-1,-1,-1,-1,-1,-1,-1,
  9,2,10,9,0,2,8,4,7,-1,-1,-1,-1,-1,-1,-1,
  2,10,9,2,9,7,2,7,3,7,9,4,-1,-1,-1,-1,
  8,4,7,3,11,2,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  11,4,7,11,2,4,2,0,4,-1,-1,-1,-1,-1,-1,-1,
  9,0,1,8,4,7,2,3,11,-1,-1,-1,-1,-1,-1,-1,
  4,7,11,9,4,11,9,11,2,9,2,1,-1,-1,-1,-1,
  3,10,1,3,11,10,7,8,4,-1,-1,-1,-1,-1,-1,-1,
  1,11,10,1,4,11,1,0,4,7,11,4,-1,-1,-1,-1,
  4,7,8,9,0,11,9,11,10,11,0,3,-1,-1,-1,-1,
  4,7,11,4,11,9,9,11,10,-1,-1,-1,-1,-1,-1,-1,
  9,5,4,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  9,5,4,0,8,3,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  0,5,4,1,5,0,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  8,5,4,8,3,5,3,1,5,-1,-1,-1,-1,-1,-1,-1,
  1,2,10,9,5,4,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  3,0,8,1,2,10,4,9,5,-1,-1,-1,-1,-1,-1,-1,
  5,2,10,5,4,2,4,0,2,-1,-1,-1,-1,-1,-1,-1,
  2,10,5,3,2,5,3,5,4,3,4,8,-1,-1,-1,-1,
  9,5,4,2,3,11,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  0,11,2,0,8,11,4,9,5,-1,-1,-1,-1,-1,-1,-1,
  0,5,4,0,1,5,2,3,11,-1,-1,-1,-1,-1,-1,-1,
  2,1,5,2,5,8,2,8,11,4,8,5,-1,-1,-1,-1,
  10,3,11,10,1,3,9,5,4,-1,-1,-1,-1,-1,-1,-1,
  4,9,5,0,8,1,8,10,1,8,11,10,-1,-1,-1,-1,
  5,4,0,5,0,11,5,11,10,11,0,3,-1,-1,-1,-1,
  5,4,8,5,8,10,10,8,11,-1,-1,-1,-1,-1,-1,-1,
  9,7,8,5,7,9,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  9,3,0,9,5,3,5,7,3,-1,-1,-1,-1,-1,-1,-1,
  0,7,8,0,1,7,1,5,7,-1,-1,-1,-1,-1,-1,-1,
  1,5,3,3,5,7,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  9,7,8,9,5,7,10,1,2,-1,-1,-1,-1,-1,-1,-1,
  10,1,2,9,5,0,5,3,0,5,7,3,-1,-1,-1,-1,
  8,0,2,8,2,5,8,5,7,10,5,2,-1,-1,-1,-1,
  2,10,5,2,5,3,3,5,7,-1,-1,-1,-1,-1,-1,-1,
  7,9,5,7,8,9,3,11,2,-1,-1,-1,-1,-1,-1,-1,
  9,5,7,9,7,2,9,2,0,2,7,11,-1,-1,-1,-1,
  2,3,11,0,1,8,1,7,8,1,5,7,-1,-1,-1,-1,
  11,2,1,11,1,7,7,1,5,-1,-1,-1,-1,-1,-1,-1,
  9,5,8,8,5,7,10,1,3,10,3,11,-1,-1,-1,-1,
  5,7,0,5,0,9,7,11,0,1,0,10,11,10,0,-1,
  11,10,0,11,0,3,10,5,0,8,0,7,5,7,0,-1,
  11,10,5,7,11,5,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  10,6,5,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  0,8,3,5,10,6,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  9,0,1,5,10,6,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  1,8,3,1,9,8,5,10,6,-1,-1,-1,-1,-1,-1,-1,
  1,6,5,2,6,1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  1,6,5,1,2,6,3,0,8,-1,-1,-1,-1,-1,-1,-1,
  9,6,5,9,0,6,0,2,6,-1,-1,-1,-1,-1,-1,-1,
  5,9,8,5,8,2,5,2,6,3,2,8,-1,-1,-1,-1,
  2,3,11,10,6,5,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  11,0,8,11,2,0,10,6,5,-1,-1,-1,-1,-1,-1,-1,
  0,1,9,2,3,11,5,10,6,-1,-1,-1,-1,-1,-1,-1,
  5,10,6,1,9,2,9,11,2,9,8,11,-1,-1,-1,-1,
  6,3,11,6,5,3,5,1,3,-1,-1,-1,-1,-1,-1,-1,
  0,8,11,0,11,5,0,5,1,5,11,6,-1,-1,-1,-1,
  3,11,6,0,3,6,0,6,5,0,5,9,-1,-1,-1,-1,
  6,5,9,6,9,11,11,9,8,-1,-1,-1,-1,-1,-1,-1,
  5,10,6,4,7,8,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  4,3,0,4,7,3,6,5,10,-1,-1,-1,-1,-1,-1,-1,
  1,9,0,5,10,6,8,4,7,-1,-1,-1,-1,-1,-1,-1,
  10,6,5,1,9,7,1,7,3,7,9,4,-1,-1,-1,-1,
  6,1,2,6,5,1,4,7,8,-1,-1,-1,-1,-1,-1,-1,
  1,2,5,5,2,6,3,0,4,3,4,7,-1,-1,-1,-1,
  8,4,7,9,0,5,0,6,5,0,2,6,-1,-1,-1,-1,
  7,3,9,7,9,4,3,2,9,5,9,6,2,6,9,-1,
  3,11,2,7,8,4,10,6,5,-1,-1,-1,-1,-1,-1,-1,
  5,10,6,4,7,2,4,2,0,2,7,11,-1,-1,-1,-1,
  0,1,9,4,7,8,2,3,11,5,10,6,-1,-1,-1,-1,
  9,2,1,9,11,2,9,4,11,7,11,4,5,10,6,-1,
  8,4,7,3,11,5,3,5,1,5,11,6,-1,-1,-1,-1,
  5,1,11,5,11,6,1,0,11,7,11,4,0,4,11,-1,
  0,5,9,0,6,5,0,3,6,11,6,3,8,4,7,-1,
  6,5,9,6,9,11,4,7,9,7,11,9,-1,-1,-1,-1,
  10,4,9,6,4,10,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  4,10,6,4,9,10,0,8,3,-1,-1,-1,-1,-1,-1,-1,
  10,0,1,10,6,0,6,4,0,-1,-1,-1,-1,-1,-1,-1,
  8,3,1,8,1,6,8,6,4,6,1,10,-1,-1,-1,-1,
  1,4,9,1,2,4,2,6,4,-1,-1,-1,-1,-1,-1,-1,
  3,0,8,1,2,9,2,4,9,2,6,4,-1,-1,-1,-1,
  0,2,4,4,2,6,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  8,3,2,8,2,4,4,2,6,-1,-1,-1,-1,-1,-1,-1,
  10,4,9,10,6,4,11,2,3,-1,-1,-1,-1,-1,-1,-1,
  0,8,2,2,8,11,4,9,10,4,10,6,-1,-1,-1,-1,
  3,11,2,0,1,6,0,6,4,6,1,10,-1,-1,-1,-1,
  6,4,1,6,1,10,4,8,1,2,1,11,8,11,1,-1,
  9,6,4,9,3,6,9,1,3,11,6,3,-1,-1,-1,-1,
  8,11,1,8,1,0,11,6,1,9,1,4,6,4,1,-1,
  3,11,6,3,6,0,0,6,4,-1,-1,-1,-1,-1,-1,-1,
  6,4,8,11,6,8,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  7,10,6,7,8,10,8,9,10,-1,-1,-1,-1,-1,-1,-1,
  0,7,3,0,10,7,0,9,10,6,7,10,-1,-1,-1,-1,
  10,6,7,1,10,7,1,7,8,1,8,0,-1,-1,-1,-1,
  10,6,7,10,7,1,1,7,3,-1,-1,-1,-1,-1,-1,-1,
  1,2,6,1,6,8,1,8,9,8,6,7,-1,-1,-1,-1,
  2,6,9,2,9,1,6,7,9,0,9,3,7,3,9,-1,
  7,8,0,7,0,6,6,0,2,-1,-1,-1,-1,-1,-1,-1,
  7,3,2,6,7,2,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  2,3,11,10,6,8,10,8,9,8,6,7,-1,-1,-1,-1,
  2,0,7,2,7,11,0,9,7,6,7,10,9,10,7,-1,
  1,8,0,1,7,8,1,10,7,6,7,10,2,3,11,-1,
  11,2,1,11,1,7,10,6,1,6,7,1,-1,-1,-1,-1,
  8,9,6,8,6,7,9,1,6,11,6,3,1,3,6,-1,
  0,9,1,11,6,7,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  7,8,0,7,0,6,3,11,0,11,6,0,-1,-1,-1,-1,
  7,11,6,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  7,6,11,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  3,0,8,11,7,6,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  0,1,9,11,7,6,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  8,1,9,8,3,1,11,7,6,-1,-1,-1,-1,-1,-1,-1,
  10,1,2,6,11,7,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  1,2,10,3,0,8,6,11,7,-1,-1,-1,-1,-1,-1,-1,
  2,9,0,2,10,9,6,11,7,-1,-1,-1,-1,-1,-1,-1,
  6,11,7,2,10,3,10,8,3,10,9,8,-1,-1,-1,-1,
  7,2,3,6,2,7,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  7,0,8,7,6,0,6,2,0,-1,-1,-1,-1,-1,-1,-1,
  2,7,6,2,3,7,0,1,9,-1,-1,-1,-1,-1,-1,-1,
  1,6,2,1,8,6,1,9,8,8,7,6,-1,-1,-1,-1,
  10,7,6,10,1,7,1,3,7,-1,-1,-1,-1,-1,-1,-1,
  10,7,6,1,7,10,1,8,7,1,0,8,-1,-1,-1,-1,
  0,3,7,0,7,10,0,10,9,6,10,7,-1,-1,-1,-1,
  7,6,10,7,10,8,8,10,9,-1,-1,-1,-1,-1,-1,-1,
  6,8,4,11,8,6,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  3,6,11,3,0,6,0,4,6,-1,-1,-1,-1,-1,-1,-1,
  8,6,11,8,4,6,9,0,1,-1,-1,-1,-1,-1,-1,-1,
  9,4,6,9,6,3,9,3,1,11,3,6,-1,-1,-1,-1,
  6,8,4,6,11,8,2,10,1,-1,-1,-1,-1,-1,-1,-1,
  1,2,10,3,0,11,0,6,11,0,4,6,-1,-1,-1,-1,
  4,11,8,4,6,11,0,2,9,2,10,9,-1,-1,-1,-1,
  10,9,3,10,3,2,9,4,3,11,3,6,4,6,3,-1,
  8,2,3,8,4,2,4,6,2,-1,-1,-1,-1,-1,-1,-1,
  0,4,2,4,6,2,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  1,9,0,2,3,4,2,4,6,4,3,8,-1,-1,-1,-1,
  1,9,4,1,4,2,2,4,6,-1,-1,-1,-1,-1,-1,-1,
  8,1,3,8,6,1,8,4,6,6,10,1,-1,-1,-1,-1,
  10,1,0,10,0,6,6,0,4,-1,-1,-1,-1,-1,-1,-1,
  4,6,3,4,3,8,6,10,3,0,3,9,10,9,3,-1,
  10,9,4,6,10,4,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  4,9,5,7,6,11,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  0,8,3,4,9,5,11,7,6,-1,-1,-1,-1,-1,-1,-1,
  5,0,1,5,4,0,7,6,11,-1,-1,-1,-1,-1,-1,-1,
  11,7,6,8,3,4,3,5,4,3,1,5,-1,-1,-1,-1,
  9,5,4,10,1,2,7,6,11,-1,-1,-1,-1,-1,-1,-1,
  6,11,7,1,2,10,0,8,3,4,9,5,-1,-1,-1,-1,
  7,6,11,5,4,10,4,2,10,4,0,2,-1,-1,-1,-1,
  3,4,8,3,5,4,3,2,5,10,5,2,11,7,6,-1,
  7,2,3,7,6,2,5,4,9,-1,-1,-1,-1,-1,-1,-1,
  9,5,4,0,8,6,0,6,2,6,8,7,-1,-1,-1,-1,
  3,6,2,3,7,6,1,5,0,5,4,0,-1,-1,-1,-1,
  6,2,8,6,8,7,2,1,8,4,8,5,1,5,8,-1,
  9,5,4,10,1,6,1,7,6,1,3,7,-1,-1,-1,-1,
  1,6,10,1,7,6,1,0,7,8,7,0,9,5,4,-1,
  4,0,10,4,10,5,0,3,10,6,10,7,3,7,10,-1,
  7,6,10,7,10,8,5,4,10,4,8,10,-1,-1,-1,-1,
  6,9,5,6,11,9,11,8,9,-1,-1,-1,-1,-1,-1,-1,
  3,6,11,0,6,3,0,5,6,0,9,5,-1,-1,-1,-1,
  0,11,8,0,5,11,0,1,5,5,6,11,-1,-1,-1,-1,
  6,11,3,6,3,5,5,3,1,-1,-1,-1,-1,-1,-1,-1,
  1,2,10,9,5,11,9,11,8,11,5,6,-1,-1,-1,-1,
  0,11,3,0,6,11,0,9,6,5,6,9,1,2,10,-1,
  11,8,5,11,5,6,8,0,5,10,5,2,0,2,5,-1,
  6,11,3,6,3,5,2,10,3,10,5,3,-1,-1,-1,-1,
  5,8,9,5,2,8,5,6,2,3,8,2,-1,-1,-1,-1,
  9,5,6,9,6,0,0,6,2,-1,-1,-1,-1,-1,-1,-1,
  1,5,8,1,8,0,5,6,8,3,8,2,6,2,8,-1,
  1,5,6,2,1,6,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  1,3,6,1,6,10,3,8,6,5,6,9,8,9,6,-1,
  10,1,0,10,0,6,9,5,0,5,6,0,-1,-1,-1,-1,
  0,3,8,5,6,10,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  10,5,6,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  11,5,10,7,5,11,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  11,5,10,11,7,5,8,3,0,-1,-1,-1,-1,-1,-1,-1,
  5,11,7,5,10,11,1,9,0,-1,-1,-1,-1,-1,-1,-1,
  10,7,5,10,11,7,9,8,1,8,3,1,-1,-1,-1,-1,
  11,1,2,11,7,1,7,5,1,-1,-1,-1,-1,-1,-1,-1,
  0,8,3,1,2,7,1,7,5,7,2,11,-1,-1,-1,-1,
  9,7,5,9,2,7,9,0,2,2,11,7,-1,-1,-1,-1,
  7,5,2,7,2,11,5,9,2,3,2,8,9,8,2,-1,
  2,5,10,2,3,5,3,7,5,-1,-1,-1,-1,-1,-1,-1,
  8,2,0,8,5,2,8,7,5,10,2,5,-1,-1,-1,-1,
  9,0,1,5,10,3,5,3,7,3,10,2,-1,-1,-1,-1,
  9,8,2,9,2,1,8,7,2,10,2,5,7,5,2,-1,
  1,3,5,3,7,5,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  0,8,7,0,7,1,1,7,5,-1,-1,-1,-1,-1,-1,-1,
  9,0,3,9,3,5,5,3,7,-1,-1,-1,-1,-1,-1,-1,
  9,8,7,5,9,7,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  5,8,4,5,10,8,10,11,8,-1,-1,-1,-1,-1,-1,-1,
  5,0,4,5,11,0,5,10,11,11,3,0,-1,-1,-1,-1,
  0,1,9,8,4,10,8,10,11,10,4,5,-1,-1,-1,-1,
  10,11,4,10,4,5,11,3,4,9,4,1,3,1,4,-1,
  2,5,1,2,8,5,2,11,8,4,5,8,-1,-1,-1,-1,
  0,4,11,0,11,3,4,5,11,2,11,1,5,1,11,-1,
  0,2,5,0,5,9,2,11,5,4,5,8,11,8,5,-1,
  9,4,5,2,11,3,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  2,5,10,3,5,2,3,4,5,3,8,4,-1,-1,-1,-1,
  5,10,2,5,2,4,4,2,0,-1,-1,-1,-1,-1,-1,-1,
  3,10,2,3,5,10,3,8,5,4,5,8,0,1,9,-1,
  5,10,2,5,2,4,1,9,2,9,4,2,-1,-1,-1,-1,
  8,4,5,8,5,3,3,5,1,-1,-1,-1,-1,-1,-1,-1,
  0,4,5,1,0,5,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  8,4,5,8,5,3,9,0,5,0,3,5,-1,-1,-1,-1,
  9,4,5,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  4,11,7,4,9,11,9,10,11,-1,-1,-1,-1,-1,-1,-1,
  0,8,3,4,9,7,9,11,7,9,10,11,-1,-1,-1,-1,
  1,10,11,1,11,4,1,4,0,7,4,11,-1,-1,-1,-1,
  3,1,4,3,4,8,1,10,4,7,4,11,10,11,4,-1,
  4,11,7,9,11,4,9,2,11,9,1,2,-1,-1,-1,-1,
  9,7,4,9,11,7,9,1,11,2,11,1,0,8,3,-1,
  11,7,4,11,4,2,2,4,0,-1,-1,-1,-1,-1,-1,-1,
  11,7,4,11,4,2,8,3,4,3,2,4,-1,-1,-1,-1,
  2,9,10,2,7,9,2,3,7,7,4,9,-1,-1,-1,-1,
  9,10,7,9,7,4,10,2,7,8,7,0,2,0,7,-1,
  3,7,10,3,10,2,7,4,10,1,10,0,4,0,10,-1,
  1,10,2,8,7,4,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  4,9,1,4,1,7,7,1,3,-1,-1,-1,-1,-1,-1,-1,
  4,9,1,4,1,7,0,8,1,8,7,1,-1,-1,-1,-1,
  4,0,3,7,4,3,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  4,8,7,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  9,10,8,10,11,8,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  3,0,9,3,9,11,11,9,10,-1,-1,-1,-1,-1,-1,-1,
  0,1,10,0,10,8,8,10,11,-1,-1,-1,-1,-1,-1,-1,
  3,1,10,11,3,10,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  1,2,11,1,11,9,9,11,8,-1,-1,-1,-1,-1,-1,-1,
  3,0,9,3,9,11,1,2,9,2,11,9,-1,-1,-1,-1,
  0,2,11,8,0,11,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  3,2,11,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  2,3,8,2,8,10,10,8,9,-1,-1,-1,-1,-1,-1,-1,
  9,10,2,0,9,2,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  2,3,8,2,8,10,0,1,8,1,10,8,-1,-1,-1,-1,
  1,10,2,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  1,3,8,9,1,8,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  0,9,1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  0,3,8,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  -1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
];

// ── WGSL Compute Shader ──────────────────────────────────────────────────────

const MC_SHADER = /* wgsl */ `
struct Grid {
  resX: u32, resY: u32, resZ: u32,
  voxelSize: f32,
  originX: f32, originY: f32, originZ: f32,
  totalVoxels: u32,
}

// Packed voxel buffer (4 voxels per u32, byte-packed) - READ ONLY for MC
@group(0) @binding(0) var<storage, read> voxels: array<u32>;
@group(0) @binding(1) var<uniform> grid: Grid;
// Edge table (256 entries)
@group(0) @binding(2) var<storage, read> edgeTable: array<u32>;
// Tri table (256 * 16 entries)
@group(0) @binding(3) var<storage, read> triTable: array<i32>;
// Output vertex buffer (position xyz + normal xyz = 6 floats per vertex)
@group(0) @binding(4) var<storage, read_write> outVertices: array<f32>;
// Atomic vertex counter
@group(0) @binding(5) var<storage, read_write> vertexCount: atomic<u32>;

fn readVoxel(x: u32, y: u32, z: u32) -> f32 {
  if (x >= grid.resX || y >= grid.resY || z >= grid.resZ) { return 0.0; }
  let idx = x + y * grid.resX + z * grid.resX * grid.resY;
  let wordIdx = idx / 4u;
  let bytePos = idx % 4u;
  let word = voxels[wordIdx];
  let byteVal = (word >> (bytePos * 8u)) & 0xFFu;
  return select(0.0, 1.0, byteVal > 0u);
}

fn getVoxelPos(x: u32, y: u32, z: u32) -> vec3<f32> {
  return vec3<f32>(
    grid.originX + f32(x) * grid.voxelSize,
    grid.originY + f32(y) * grid.voxelSize,
    grid.originZ + f32(z) * grid.voxelSize
  );
}

fn interpolateEdge(p1: vec3<f32>, p2: vec3<f32>, v1: f32, v2: f32) -> vec3<f32> {
  let iso = 0.5;
  if (abs(v1 - v2) < 0.00001) { return p1; }
  let t = (iso - v1) / (v2 - v1);
  return p1 + t * (p2 - p1);
}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  // Each thread processes one cube cell (cell at corner x,y,z)
  // Grid boundary cells (cx=resX-1 etc.) use readVoxel bounds check (returns 0.0)
  // to generate the outer surface of the stock
  let cellIdx = gid.x + gid.y * 65535u * 256u;
  let maxCells = grid.resX * grid.resY * grid.resZ;
  if (cellIdx >= maxCells) { return; }

  let cx = cellIdx % grid.resX;
  let cy = (cellIdx / grid.resX) % grid.resY;
  let cz = cellIdx / (grid.resX * grid.resY);

  // Read 8 corner values
  let v0 = readVoxel(cx, cy, cz);
  let v1 = readVoxel(cx + 1u, cy, cz);
  let v2 = readVoxel(cx + 1u, cy + 1u, cz);
  let v3 = readVoxel(cx, cy + 1u, cz);
  let v4 = readVoxel(cx, cy, cz + 1u);
  let v5 = readVoxel(cx + 1u, cy, cz + 1u);
  let v6 = readVoxel(cx + 1u, cy + 1u, cz + 1u);
  let v7 = readVoxel(cx, cy + 1u, cz + 1u);

  // Compute cube index
  var cubeIdx = 0u;
  if (v0 > 0.5) { cubeIdx |= 1u; }
  if (v1 > 0.5) { cubeIdx |= 2u; }
  if (v2 > 0.5) { cubeIdx |= 4u; }
  if (v3 > 0.5) { cubeIdx |= 8u; }
  if (v4 > 0.5) { cubeIdx |= 16u; }
  if (v5 > 0.5) { cubeIdx |= 32u; }
  if (v6 > 0.5) { cubeIdx |= 64u; }
  if (v7 > 0.5) { cubeIdx |= 128u; }

  // Skip fully inside or fully outside
  let edges = edgeTable[cubeIdx];
  if (edges == 0u) { return; }

  // Corner positions
  let p0 = getVoxelPos(cx, cy, cz);
  let p1 = getVoxelPos(cx + 1u, cy, cz);
  let p2 = getVoxelPos(cx + 1u, cy + 1u, cz);
  let p3 = getVoxelPos(cx, cy + 1u, cz);
  let p4 = getVoxelPos(cx, cy, cz + 1u);
  let p5 = getVoxelPos(cx + 1u, cy, cz + 1u);
  let p6 = getVoxelPos(cx + 1u, cy + 1u, cz + 1u);
  let p7 = getVoxelPos(cx, cy + 1u, cz + 1u);

  // Interpolate edge vertices
  var edgeVerts: array<vec3<f32>, 12>;
  if ((edges & 1u) != 0u)    { edgeVerts[0]  = interpolateEdge(p0, p1, v0, v1); }
  if ((edges & 2u) != 0u)    { edgeVerts[1]  = interpolateEdge(p1, p2, v1, v2); }
  if ((edges & 4u) != 0u)    { edgeVerts[2]  = interpolateEdge(p2, p3, v2, v3); }
  if ((edges & 8u) != 0u)    { edgeVerts[3]  = interpolateEdge(p3, p0, v3, v0); }
  if ((edges & 16u) != 0u)   { edgeVerts[4]  = interpolateEdge(p4, p5, v4, v5); }
  if ((edges & 32u) != 0u)   { edgeVerts[5]  = interpolateEdge(p5, p6, v5, v6); }
  if ((edges & 64u) != 0u)   { edgeVerts[6]  = interpolateEdge(p6, p7, v6, v7); }
  if ((edges & 128u) != 0u)  { edgeVerts[7]  = interpolateEdge(p7, p4, v7, v4); }
  if ((edges & 256u) != 0u)  { edgeVerts[8]  = interpolateEdge(p0, p4, v0, v4); }
  if ((edges & 512u) != 0u)  { edgeVerts[9]  = interpolateEdge(p1, p5, v1, v5); }
  if ((edges & 1024u) != 0u) { edgeVerts[10] = interpolateEdge(p2, p6, v2, v6); }
  if ((edges & 2048u) != 0u) { edgeVerts[11] = interpolateEdge(p3, p7, v3, v7); }

  // Emit triangles
  let triBase = cubeIdx * 16u;
  for (var i = 0u; i < 15u; i += 3u) {
    let e0 = triTable[triBase + i];
    if (e0 < 0) { break; }
    let e1 = triTable[triBase + i + 1u];
    let e2 = triTable[triBase + i + 2u];

    let va = edgeVerts[e0];
    let vb = edgeVerts[e1];
    let vc = edgeVerts[e2];

    // Compute face normal
    let ab = vb - va;
    let ac = vc - va;
    let normal = normalize(cross(ab, ac));

    // Atomic append 3 vertices (6 floats each: pos xyz + normal xyz)
    let baseVert = atomicAdd(&vertexCount, 3u);
    let baseF = baseVert * 6u;

    // Vertex 0
    outVertices[baseF]      = va.x;
    outVertices[baseF + 1u] = va.y;
    outVertices[baseF + 2u] = va.z;
    outVertices[baseF + 3u] = normal.x;
    outVertices[baseF + 4u] = normal.y;
    outVertices[baseF + 5u] = normal.z;
    // Vertex 1
    outVertices[baseF + 6u]  = vb.x;
    outVertices[baseF + 7u]  = vb.y;
    outVertices[baseF + 8u]  = vb.z;
    outVertices[baseF + 9u]  = normal.x;
    outVertices[baseF + 10u] = normal.y;
    outVertices[baseF + 11u] = normal.z;
    // Vertex 2
    outVertices[baseF + 12u] = vc.x;
    outVertices[baseF + 13u] = vc.y;
    outVertices[baseF + 14u] = vc.z;
    outVertices[baseF + 15u] = normal.x;
    outVertices[baseF + 16u] = normal.y;
    outVertices[baseF + 17u] = normal.z;
  }
}
`;

// ── Main Class ───────────────────────────────────────────────────────────────

export class MarchingCubesGPU {
  private device: GPUDevice;
  private voxelBuffer: GPUBuffer;
  private resX: number;
  private resY: number;
  private resZ: number;
  private voxelSize: number;
  private origin: [number, number, number];

  // MC-specific buffers
  private gridUniformBuffer: GPUBuffer | null = null;
  private edgeTableBuffer: GPUBuffer | null = null;
  private triTableBuffer: GPUBuffer | null = null;
  private outputVertexBuffer: GPUBuffer | null = null;
  private vertexCountBuffer: GPUBuffer | null = null;
  private vertexCountStagingBuffer: GPUBuffer | null = null;
  private outputStagingBuffer: GPUBuffer | null = null;
  private outputStagingBufferSize = 0;

  private pipeline: GPUComputePipeline | null = null;
  private bindGroup: GPUBindGroup | null = null;

  private maxOutputVertices: number;
  private initialized = false;

  // Reusable geometry (avoid re-creating every frame)
  private _geometry: THREE.BufferGeometry | null = null;
  private _positionAttr: THREE.BufferAttribute | null = null;
  private _normalAttr: THREE.BufferAttribute | null = null;

  // Pre-allocated zero buffer for vertex count reset
  private readonly vertexCountZero = new Uint32Array([0]);

  constructor(
    device: GPUDevice,
    voxelBuffer: GPUBuffer,
    resX: number, resY: number, resZ: number,
    voxelSize: number,
    origin: [number, number, number]
  ) {
    this.device = device;
    this.voxelBuffer = voxelBuffer;
    this.resX = resX;
    this.resY = resY;
    this.resZ = resZ;
    this.voxelSize = voxelSize;
    this.origin = origin;

    // Max output: 5 triangles per cell * 3 vertices = 15 vertices per cell
    // But typical surface cells are <5% of total, so we use a conservative estimate
    const totalCells = resX * resY * resZ;
    // Estimate: ~10% of cells are surface cells, avg 2 triangles each
    this.maxOutputVertices = Math.min(
      Math.ceil(totalCells * 0.15) * 3 * 2, // Conservative estimate
      16 * 1024 * 1024 // 16M vertices max (~384 MB)
    );
  }

  async initialize(): Promise<boolean> {
    try {
      this.createBuffers();
      await this.createPipeline();
      this.initialized = true;
      return true;
    } catch (error) {
      console.error('[MarchingCubesGPU] Init failed:', error);
      return false;
    }
  }

  private createBuffers(): void {
    // Grid uniform (32 bytes: 3 u32 + 1 f32 + 3 f32 + 1 u32)
    this.gridUniformBuffer = this.device.createBuffer({
      size: 32,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    const gridData = new ArrayBuffer(32);
    const gridU32 = new Uint32Array(gridData);
    const gridF32 = new Float32Array(gridData);
    gridU32[0] = this.resX; gridU32[1] = this.resY; gridU32[2] = this.resZ;
    gridF32[3] = this.voxelSize;
    gridF32[4] = this.origin[0]; gridF32[5] = this.origin[1]; gridF32[6] = this.origin[2];
    gridU32[7] = this.resX * this.resY * this.resZ;
    this.device.queue.writeBuffer(this.gridUniformBuffer, 0, gridData);

    // Edge table (256 * 4 bytes)
    this.edgeTableBuffer = this.device.createBuffer({
      size: 256 * 4,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    this.device.queue.writeBuffer(this.edgeTableBuffer, 0, new Uint32Array(MC_EDGE_TABLE));

    // Tri table (256 * 16 * 4 bytes)
    this.triTableBuffer = this.device.createBuffer({
      size: 256 * 16 * 4,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    this.device.queue.writeBuffer(this.triTableBuffer, 0, new Int32Array(MC_TRI_TABLE));

    // Output vertex buffer (6 floats per vertex: pos xyz + normal xyz)
    const outputSize = this.maxOutputVertices * 6 * 4;
    this.outputVertexBuffer = this.device.createBuffer({
      size: outputSize,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
    });

    // Vertex count atomic buffer (1 x u32)
    this.vertexCountBuffer = this.device.createBuffer({
      size: 4,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC | GPUBufferUsage.COPY_DST,
    });

    // Staging buffers for readback
    this.vertexCountStagingBuffer = this.device.createBuffer({
      size: 4,
      usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
    });

    // Output staging (allocated on demand based on actual vertex count)
    // We'll use a max-size staging buffer
    const maxStagingSize = Math.min(outputSize, 128 * 1024 * 1024); // Max 128MB staging
    this.outputStagingBufferSize = maxStagingSize;
    this.outputStagingBuffer = this.device.createBuffer({
      size: maxStagingSize,
      usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
    });
  }

  private async createPipeline(): Promise<void> {
    const module = this.device.createShaderModule({ code: MC_SHADER });
    this.pipeline = this.device.createComputePipeline({
      layout: 'auto',
      compute: { module, entryPoint: 'main' },
    });

    // Create bind group once - buffers don't change, only their contents
    // The GPU references the buffer OBJECTS, not snapshots of their data
    this.bindGroup = this.device.createBindGroup({
      layout: this.pipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: this.voxelBuffer } },
        { binding: 1, resource: { buffer: this.gridUniformBuffer! } },
        { binding: 2, resource: { buffer: this.edgeTableBuffer! } },
        { binding: 3, resource: { buffer: this.triTableBuffer! } },
        { binding: 4, resource: { buffer: this.outputVertexBuffer! } },
        { binding: 5, resource: { buffer: this.vertexCountBuffer! } },
      ],
    });
  }

  /**
   * Generate mesh from current voxel buffer state.
   * This is the main entry point called by the component.
   * Returns a Three.js BufferGeometry with positions and normals.
   *
   * NOTE: The returned geometry reuses internal buffers for performance.
   * Do NOT dispose the returned geometry - it will be reused on next call.
   */
  async generateMesh(): Promise<THREE.BufferGeometry | null> {
    if (!this.initialized || !this.pipeline || !this.outputVertexBuffer || !this.bindGroup) return null;

    try {
      // Reset vertex counter to 0 (using pre-allocated buffer)
      this.device.queue.writeBuffer(this.vertexCountBuffer!, 0, this.vertexCountZero);

      // Dispatch compute - one thread per cell
      const totalCells = this.resX * this.resY * this.resZ;
      const workgroups = Math.ceil(totalCells / 256);

      const encoder = this.device.createCommandEncoder();
      const pass = encoder.beginComputePass();
      pass.setPipeline(this.pipeline);
      pass.setBindGroup(0, this.bindGroup);

      if (workgroups <= 65535) {
        pass.dispatchWorkgroups(workgroups);
      } else {
        // 2D dispatch for large grids
        const dispatchX = 65535;
        const dispatchY = Math.ceil(workgroups / 65535);
        pass.dispatchWorkgroups(dispatchX, dispatchY);
      }
      pass.end();

      // Copy BOTH vertex count AND vertex data in SINGLE encoder (reduces pipeline stalls)
      encoder.copyBufferToBuffer(this.vertexCountBuffer!, 0, this.vertexCountStagingBuffer!, 0, 4);

      // Copy full vertex output buffer (we'll trim after reading count)
      const maxCopyBytes = Math.min(
        this.maxOutputVertices * 6 * 4,
        this.outputStagingBufferSize
      );
      encoder.copyBufferToBuffer(this.outputVertexBuffer, 0, this.outputStagingBuffer!, 0, maxCopyBytes);

      this.device.queue.submit([encoder.finish()]);

      // Map BOTH staging buffers in PARALLEL (single pipeline stall instead of two)
      await Promise.all([
        this.vertexCountStagingBuffer!.mapAsync(GPUMapMode.READ),
        this.outputStagingBuffer!.mapAsync(GPUMapMode.READ, 0, maxCopyBytes)
      ]);

      // Read vertex count
      const countData = new Uint32Array(this.vertexCountStagingBuffer!.getMappedRange());
      const vertCount = countData[0];
      this.vertexCountStagingBuffer!.unmap();

      if (vertCount === 0) {
        this.outputStagingBuffer!.unmap();
        return this.createEmptyGeometry();
      }

      // Clamp to max (in case atomic overflowed)
      const safeCount = Math.min(vertCount, this.maxOutputVertices);
      const vertexBytes = safeCount * 6 * 4;

      // Read vertex data (already mapped)
      const rawData = new Float32Array(this.outputStagingBuffer!.getMappedRange(0, vertexBytes));

      // Deinterleave positions and normals
      const positions = new Float32Array(safeCount * 3);
      const normals = new Float32Array(safeCount * 3);
      for (let i = 0; i < safeCount; i++) {
        const src = i * 6;
        const dst = i * 3;
        positions[dst] = rawData[src];
        positions[dst + 1] = rawData[src + 1];
        positions[dst + 2] = rawData[src + 2];
        normals[dst] = rawData[src + 3];
        normals[dst + 1] = rawData[src + 4];
        normals[dst + 2] = rawData[src + 5];
      }

      this.outputStagingBuffer!.unmap();

      // Reuse existing geometry and attributes to avoid allocations
      if (!this._geometry) {
        this._geometry = new THREE.BufferGeometry();
        this._positionAttr = new THREE.BufferAttribute(positions, 3);
        this._normalAttr = new THREE.BufferAttribute(normals, 3);
        this._geometry.setAttribute('position', this._positionAttr);
        this._geometry.setAttribute('normal', this._normalAttr);
      } else {
        // Update existing attributes if size matches, otherwise recreate
        if (this._positionAttr!.array.length === positions.length) {
          (this._positionAttr!.array as Float32Array).set(positions);
          this._positionAttr!.needsUpdate = true;
          (this._normalAttr!.array as Float32Array).set(normals);
          this._normalAttr!.needsUpdate = true;
        } else {
          // Size changed, recreate attributes
          this._positionAttr = new THREE.BufferAttribute(positions, 3);
          this._normalAttr = new THREE.BufferAttribute(normals, 3);
          this._geometry.setAttribute('position', this._positionAttr);
          this._geometry.setAttribute('normal', this._normalAttr);
        }
      }

      // Update draw range and bounding sphere
      this._geometry.setDrawRange(0, safeCount);
      this._geometry.computeBoundingSphere();

      return this._geometry;

    } catch (error) {
      console.error('[MarchingCubesGPU] generateMesh failed:', error);
      return null;
    }
  }

  private createEmptyGeometry(): THREE.BufferGeometry {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(0), 3));
    geometry.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(0), 3));
    return geometry;
  }

  isInitialized(): boolean { return this.initialized; }

  dispose(): void {
    this.gridUniformBuffer?.destroy();
    this.edgeTableBuffer?.destroy();
    this.triTableBuffer?.destroy();
    this.outputVertexBuffer?.destroy();
    this.vertexCountBuffer?.destroy();
    this.vertexCountStagingBuffer?.destroy();
    this.outputStagingBuffer?.destroy();
    this.bindGroup = null;
    this.pipeline = null;
    this.initialized = false;
    this._geometry?.dispose();
    this._geometry = null;
  }
}

// ── Factory ──────────────────────────────────────────────────────────────────

export async function createMarchingCubesGPU(
  device: GPUDevice,
  voxelBuffer: GPUBuffer,
  resX: number, resY: number, resZ: number,
  voxelSize: number,
  origin: [number, number, number]
): Promise<MarchingCubesGPU | null> {
  const mc = new MarchingCubesGPU(device, voxelBuffer, resX, resY, resZ, voxelSize, origin);
  const ok = await mc.initialize();
  if (!ok) { mc.dispose(); return null; }
  return mc;
}
